import { trpc } from "@/lib/trpc";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { resolveCartId } from "@/lib/cartRouting";

type CartBuyableProduct = {
  variantId?: string | null;
  title?: string;
  shortTitle?: string;
};

export type CartLineItem = {
  id: string;
  title: string;
  thumbnail: string | null;
  variantTitle: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  variantId: string | null;
  productId: string | null;
  productHandle: string | null;
  inventoryQuantity: number | null;
  requiresShipping: boolean;
};

export type CheckoutCustomer = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  shippingMethod: string;
  notes: string;
};

export type CompletedOrder = {
  id: string;
  displayId: string | null;
  createdAt: string | null;
  email: string | null;
  total: number;
  currencyCode: string;
  items: CartLineItem[];
  raw: any;
};

type CartSnapshot = {
  id: string;
  regionId: string;
  currencyCode: string;
  email: string | null;
  itemCount: number;
  subtotal: number;
  shippingTotal: number;
  taxTotal: number;
  total: number;
  items: CartLineItem[];
  shippingAddress: any;
  billingAddress: any;
  shippingMethods: Array<{ id: string; name: string; amount: number }>;
  paymentProviders: string[];
  paymentSessions: string[];
  raw: any;
};

type CartContextValue = {
  cartId: string | null;
  cart: CartSnapshot | null;
  items: CartLineItem[];
  itemCount: number;
  subtotal: number;
  total: number;
  isLoading: boolean;
  isCartOpen: boolean;
  lastCompletedOrder: CompletedOrder | null;
  lastOrder: CompletedOrder | null;
  setCartOpen: (open: boolean) => void;
  openCart: () => void;
  closeCart: () => void;
  addItem: (product: CartBuyableProduct, quantity?: number) => Promise<CartSnapshot | undefined>;
  startFreshCheckout: (items: Array<{ variantId: string; quantity: number }>) => Promise<CartSnapshot>;
  updateQuantity: (lineItemId: string, quantity: number) => void;
  removeItem: (lineItemId: string) => void;
  clearCart: () => Promise<void>;
  resetCart: () => void;
  refreshCart: () => Promise<void>;
  storeCompletedOrder: (order: CompletedOrder) => void;
  clearLastCompletedOrder: () => void;
  clearLastOrder: () => void;
  entries: Array<{ handle: string; quantity: number }>;
};

const CART_STORAGE_KEY = "kratown-medusa-cart-id";
const ORDER_STORAGE_KEY = "kratown-medusa-last-order";

const CartContext = createContext<CartContextValue | undefined>(undefined);

function parseStoredJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") {
    return fallback;
  }

  const raw = window.localStorage.getItem(key);
  if (!raw) {
    return fallback;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readStoredCartId() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem(CART_STORAGE_KEY);
}

function getInitialCartId() {
  if (typeof window === "undefined") {
    return null;
  }

  return resolveCartId(window.location.search, readStoredCartId());
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const utils = trpc.useUtils();
  const [cartId, setCartId] = useState<string | null>(getInitialCartId);
  const [isCartOpen, setCartOpen] = useState(false);
  const [lastCompletedOrder, setLastCompletedOrder] = useState<CompletedOrder | null>(() =>
    parseStoredJson<CompletedOrder | null>(ORDER_STORAGE_KEY, null),
  );
  const cartQueryInput = useMemo(() => ({ cartId }), [cartId]);

  const cartQuery = trpc.medusaCart.get.useQuery(
    cartQueryInput,
    {
      enabled: Boolean(cartId),
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );

  const resetCart = useCallback(() => {
    setCartId(null);
    setCartOpen(false);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, []);

  const syncCart = (nextCart: CartSnapshot) => {
    setCartId(nextCart.id);
    utils.medusaCart.get.setData({ cartId: nextCart.id }, nextCart);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CART_STORAGE_KEY, nextCart.id);
    }
  };

  useEffect(() => {
    if (!cartQuery.data?.id) {
      return;
    }

    syncCart(cartQuery.data as CartSnapshot);
  }, [cartQuery.data?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (lastCompletedOrder) {
      window.localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(lastCompletedOrder));
      return;
    }

    window.localStorage.removeItem(ORDER_STORAGE_KEY);
  }, [lastCompletedOrder]);

  const addItemMutation = trpc.medusaCart.addItem.useMutation();
  const startFreshCheckoutMutation = trpc.medusaCart.startFreshCheckout.useMutation();

  const updateItemMutation = trpc.medusaCart.updateItem.useMutation({
    onSuccess(data) {
      syncCart(data as CartSnapshot);
    },
  });

  const removeItemMutation = trpc.medusaCart.removeItem.useMutation({
    onSuccess(data) {
      const nextCart = data as CartSnapshot;
      if (nextCart.itemCount === 0) {
        resetCart();
        return;
      }
      syncCart(nextCart);
    },
  });

  const cart = cartId ? ((cartQuery.data as CartSnapshot | undefined) ?? null) : null;
  const items = cart?.items ?? [];
  const itemCount = cart?.itemCount ?? 0;
  const subtotal = cart?.subtotal ?? 0;
  const total = cart?.total ?? 0;

  const addItem = async (product: CartBuyableProduct, quantity = 1) => {
    if (!product.variantId) {
      return undefined;
    }

    const nextCart = (await addItemMutation.mutateAsync({
      cartId,
      variantId: product.variantId,
      quantity: Math.max(1, quantity),
    })) as CartSnapshot;
    syncCart(nextCart);
    setCartOpen(true);
    return nextCart;
  };

  const startFreshCheckout = async (checkoutItems: Array<{ variantId: string; quantity: number }>) => {
    if (!checkoutItems.length) {
      throw new Error("Add at least one purchasable item before starting checkout.");
    }

    const nextCart = (await startFreshCheckoutMutation.mutateAsync({
      items: checkoutItems.map((item) => ({
        variantId: item.variantId,
        quantity: Math.max(1, item.quantity),
      })),
    })) as CartSnapshot;
    syncCart(nextCart);
    return nextCart;
  };

  const updateQuantity = (lineItemId: string, quantity: number) => {
    if (!cart?.id) {
      return;
    }

    if (quantity <= 0) {
      removeItemMutation.mutate({
        cartId: cart.id,
        lineItemId,
      });
      return;
    }

    updateItemMutation.mutate({
      cartId: cart.id,
      lineItemId,
      quantity,
    });
  };

  const removeItem = (lineItemId: string) => {
    if (!cart?.id) {
      return;
    }

    removeItemMutation.mutate({
      cartId: cart.id,
      lineItemId,
    });
  };

  const clearCart = async () => {
    if (!cart?.id || items.length === 0) {
      resetCart();
      return;
    }

    for (const item of items) {
      await removeItemMutation.mutateAsync({
        cartId: cart.id,
        lineItemId: item.id,
      });
    }

    resetCart();
  };

  const refreshCart = async () => {
    await cartQuery.refetch();
  };

  const value = useMemo<CartContextValue>(
    () => ({
      cartId,
      cart,
      items,
      itemCount,
      subtotal,
      total,
      isLoading:
        cartQuery.isLoading ||
        addItemMutation.isPending ||
        startFreshCheckoutMutation.isPending ||
        updateItemMutation.isPending ||
        removeItemMutation.isPending,
      isCartOpen,
      lastCompletedOrder,
      lastOrder: lastCompletedOrder,
      setCartOpen,
      openCart: () => setCartOpen(true),
      closeCart: () => setCartOpen(false),
      addItem,
      startFreshCheckout,
      updateQuantity,
      removeItem,
      clearCart,
      resetCart,
      refreshCart,
      storeCompletedOrder: (order) => setLastCompletedOrder(order),
      clearLastCompletedOrder: () => setLastCompletedOrder(null),
      clearLastOrder: () => setLastCompletedOrder(null),
      entries: items.map((item) => ({ handle: item.productHandle || item.id, quantity: item.quantity })),
    }),
    [
      addItemMutation.isPending,
      cart,
      cartId,
      cartQuery.isLoading,
      isCartOpen,
      itemCount,
      items,
      lastCompletedOrder,
      removeItemMutation.isPending,
      resetCart,
      startFreshCheckoutMutation.isPending,
      subtotal,
      total,
      updateItemMutation.isPending,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }

  return context;
}
